import { authFailureResponse, isInvalidToken } from 'api/inc-api.mock-utils';
import { rest } from 'msw';
import { makeListResults } from 'test-util/api/response';
import { getEnv } from 'util/getEnv';
import {
  clientCuisine,
  benchmarkCuisinesFixture,
} from './fixtures/client-cuisine.fixture';
import { GUEST_JOURNEY_DEFAULT_THRESHOLDS } from 'api/reports-service/guest-journey-settings.utils';
import { clientConfig } from './fixtures/client-config.fixture';
import { addNewClientDomainSuccessResponse } from './fixtures/client-email-config.fixture';
import { client } from './fixtures/client.fixture';
import { employees } from './fixtures/employees.fixture';
import { getMerchant, merchants } from './fixtures/merchants.fixture';
import { personas } from './fixtures/personas.fixture';
import { roles } from './fixtures/roles.fixture';
import { searchPermissions } from './fixtures/search-permissions.fixture';
import { stores } from './fixtures/stores.fixture';
import { timezones } from './fixtures/timezones.fixture';
import { users } from './fixtures/users.fixture';

import { categories } from './fixtures/categories.fixture';
import {
  clientEmailConfig,
  validEmailSuccessResponse,
} from './fixtures/client-email-config.fixture';
import {
  clientDomains,
  newClientDomain,
} from './fixtures/clientdomain.fixture';
import {
  createPromoCode,
  getPromoCode,
  promoCodes,
} from './fixtures/promocodes.fixture';
import {
  guestJourneySettingsFixture,
  guestJourneySettingsHistoryFixture,
} from 'api/reports-service/fixtures/settings.fixture';
import { GuestJourneySettings } from 'api/reports-service/guest-journey-reports.types';
import { passwordlessConfig } from './fixtures/update-passwordless-config.fixture';
import { webAppConfigResponseFixture } from './fixtures/web-app-config.fixture';

/**
 * In-memory Guest Journey settings store so the Settings page's save actually
 * persists: the POST updates this, and the GET reads from it (seeded from the
 * fixture, resets on reload). Cloned so the fixture itself is never mutated.
 */
let guestJourneySettingsStore: GuestJourneySettings = {
  ...guestJourneySettingsFixture,
};

export const getAdminMock = fn =>
  rest.get(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/:clientId/admins/:adminId`,
    fn,
  );

export const getClientConfigMock = fn =>
  rest.get(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/:clientId/config`,
    fn,
  );

/**
 * No service prefix: admin-api serves this one itself. See `webAppConfigUrl` — a mock
 * registered under `incentivio-client-domain` would never match.
 */
export const getWebAppConfigMock = fn =>
  rest.get(
    `${getEnv('REACT_APP_ADMIN_API_URL')}/clients/:clientId/webappconfig`,
    fn,
  );

export const updateClientConfigMock = fn =>
  rest.put(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/:clientId/clientconfig`,
    fn,
  );

export const listMerchantsMock = fn =>
  rest.get(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/:clientId/merchants`,
    fn,
  );

export const listRolesMock = fn =>
  rest.get(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/:clientId/searchroles`,
    fn,
  );

export const findRoleMock = fn =>
  rest.get(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/:clientId/findrole/:id`,
    fn,
  );

export const updateRoleMock = fn =>
  rest.put(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/:clientId/updaterole`,
    fn,
  );

export const createRoleMock = fn =>
  rest.post(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/:clientId/createrole`,
    fn,
  );

export const deleteRoleMock = fn =>
  rest.delete(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/:clientId/deleterole/:roleId`,
    fn,
  );

export const searchPermissionsMock = fn =>
  rest.get(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/:clientId/searchpermissions`,
    fn,
  );

export const createStoreMock = fn =>
  rest.post(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/:clientId/merchants/:merchantId/stores`,
    fn,
  );

export const updateStoreMock = fn =>
  rest.put(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/:clientId/merchants/:merchantId/stores/:storeId`,
    fn,
  );

export const getStoreMock = fn =>
  rest.get(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/:clientId/merchants/:merchantId/stores/:storeId`,
    fn,
  );

export const listStoresMock = fn =>
  rest.get(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/:clientId/merchants/:merchantId/stores`,
    fn,
  );

export const listAdminStoresOverallMock = fn =>
  rest.get(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/admin/stores/overall`,
    fn,
  );

export const listEmployeesMock = fn =>
  rest.get(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/:clientId/admins`,
    fn,
  );

export const createEmployeeMock = fn =>
  rest.post(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/:clientId/admins`,
    fn,
  );

export const updateEmployeeMock = fn =>
  rest.put(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/:clientId/admins/:adminId`,
    fn,
  );

export const deleteEmployeeMock = fn =>
  rest.delete(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/:clientId/admins/:adminId`,
    fn,
  );

export const listCategoryMock = fn =>
  rest.get(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/:clientId/categories`,
    fn,
  );

export const listTimezonesMock = fn =>
  rest.get(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/:clientId/timezones`,
    fn,
  );

export const getClientMock = fn =>
  rest.get(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/:clientId`,
    fn,
  );

export const updateClientMock = fn =>
  rest.put(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/:clientId`,
    fn,
  );

export const resetPasswordMock = fn =>
  rest.post(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/:client/admins/:email/requestpwreset`,
    fn,
  );

export const verifyPwResetRequestMock = fn =>
  rest.get(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/verifypwresetrequest`,
    fn,
  );

export const resetPasswordSubmitMock = fn =>
  rest.post(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/resetpassword`,
    fn,
  );

export const listPersonasMock = fn =>
  rest.get(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/:clientId/personas`,
    fn,
  );

export const listPromoCodesMock = fn =>
  rest.get(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/:clientId/promocode`,
    fn,
  );

export const createPromoCodeMock = fn =>
  rest.post(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/:clientId/promocode`,
    fn,
  );

export const deletePromoCodeMock = fn =>
  rest.delete(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/:clientId/promocode/:promoCodeId`,
    fn,
  );

export const updatePromoCodeMock = fn =>
  rest.put(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/:clientId/promocode/:promoCodeId`,
    fn,
  );

export const getPromoCodeMock = fn =>
  rest.get(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/:clientId/promocode/:promoCodeId`,
    fn,
  );

export const getEmailConfigMock = fn =>
  rest.get(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/email-config`,
    fn,
  );

export const clientConfigEmailMock = fn =>
  rest.put(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/email-config`,
    fn,
  );

export const updatePasswordlessConfigMock = fn =>
  rest.put(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/passwordless-authentication-settings`,
    fn,
  );

export const getPasswordlessFileStatusMock = fn =>
  rest.get(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/passwordless-authentication-settings/file-status`,
    fn,
  );

export const addClientEmailDomainMock = fn =>
  rest.post(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/email-config/client-domain`,
    fn,
  );

export const verifyDomainMock = fn =>
  rest.post(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/email-config/verify-domain`,
    fn,
  );

export const getMerchantMock = fn =>
  rest.get(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/:clientId/merchants/:merchantId`,
    fn,
  );

export const createMerchantMock = fn =>
  rest.post(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/:clientId/merchants`,
    fn,
  );

export const updateMerchantMock = fn =>
  rest.put(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/:clientId/merchants/:merchantId`,
    fn,
  );

export const verifyAdminEmailMock = fn =>
  rest.get(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/verifyemail*`,
    fn,
  );

export const verifyEmployeeMock = fn =>
  rest.post(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/:clientId/admins/:adminId/verify`,
    fn,
  );

export const createClientDomainMock = fn =>
  rest.post(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clientdomain`,
    fn,
  );

export const deleteClientDomainMock = fn =>
  rest.delete(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clientdomain/:id`,
    fn,
  );

export const getClientDomainsByClientIdMock = fn =>
  rest.get(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clientdomain`,
    fn,
  );

export const cancelClientDomainMock = fn =>
  rest.post(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clientdomain/:id/cancel`,
    fn,
  );

export const getClientDomainMock = fn =>
  rest.get(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clientdomain/:id`,
    fn,
  );

export const getClientCuisineMock = fn =>
  rest.get(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/guest-journey/cuisine`,
    fn,
  );

export const getClientCuisineListMock = fn =>
  rest.get(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/clients/cuisine/list`,
    fn,
  );

export const getGuestJourneySettingsMock = fn =>
  rest.get(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/guest-journey/settings`,
    fn,
  );

export const getGuestJourneySettingsDefaultsMock = fn =>
  rest.get(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/guest-journey/settings/defaults`,
    fn,
  );

export const saveGuestJourneySettingsMock = fn =>
  rest.post(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/guest-journey/settings`,
    fn,
  );

export const resetGuestJourneySettingsMock = fn =>
  rest.post(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/guest-journey/settings/reset`,
    fn,
  );

export const getGuestJourneySettingsHistoryMock = fn =>
  rest.get(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-client-domain/guest-journey/settings/history`,
    fn,
  );

export const handlers = () => [
  getAdminMock((req, res, ctx) => {
    // Perform this auth check here since it is the first api call we make when
    // we load the app. This allows us to test refresh token functionality
    if (isInvalidToken(req)) return authFailureResponse(req, res, ctx);

    const user = users[req.params.adminId];

    if (user) {
      return res(ctx.json(user));
    } else {
      return res(ctx.status(404));
    }
  }),

  getClientConfigMock((_req, res, ctx) => {
    return res(
      ctx.json({
        ...(updateClientConfigBody ?? clientConfig),
        passwordlessAuthentication:
          updatePasswordlessBody ??
          (updateClientConfigBody ?? clientConfig).passwordlessAuthentication ??
          passwordlessConfig,
      }),
    );
  }),

  getWebAppConfigMock((_req, res, ctx) =>
    res(ctx.json(webAppConfigResponseFixture())),
  ),

  updateClientConfigMock((req, res, ctx) => {
    updateClientConfigBody = req.body;
    return res(ctx.json(req.body));
  }),

  listMerchantsMock((_req, res, ctx) => {
    return res(ctx.json(merchants));
  }),

  listRolesMock((req, res, ctx) => {
    return res(
      ctx.json(
        makeListResults(roles.roles, req.url.searchParams, {
          listName: 'roles',
        }),
      ),
    );
  }),

  findRoleMock((req, res, ctx) => {
    const foundRole = roles.roles.find(
      role => role.id === parseInt(req.params.id),
    );
    return !!foundRole
      ? res(ctx.json(foundRole))
      : res(
          ctx.delay(200),
          ctx.status(404),
          ctx.set({
            'incentivio-code': 'NOT_FOUND',
            'incentivio-message': 'Not Found',
          }),
        );
  }),

  updateRoleMock((_req, res, ctx) => {
    return res(ctx.status(200));
  }),

  createRoleMock((_req, res, ctx) => {
    return res(ctx.json({ id: '31' }));
  }),

  deleteRoleMock((_req, res, ctx) => {
    return res(ctx.status(200));
  }),

  searchPermissionsMock((_req, res, ctx) => {
    return res(ctx.json(searchPermissions));
  }),

  createStoreMock((_req, res, ctx) => {
    return res(ctx.json({ storeId: '985487d9-f4bf-4f57-a964-436d2fca267a' }));
  }),

  updateStoreMock((_req, res, ctx) => {
    return res(ctx.status(200));
  }),

  getStoreMock((req, res, ctx) => {
    const store = stores.stores.find(
      store => store.storeId === req.params.storeId,
    );
    return !!store ? res(ctx.json(store)) : res(ctx.status(404));
  }),

  listStoresMock((req, res, ctx) => {
    return res(
      ctx.json(
        makeListResults(stores.stores, req.url.searchParams, {
          listName: 'stores',
        }),
      ),
    );
  }),

  listAdminStoresOverallMock((_req, res, ctx) => {
    return res(ctx.json({ stores: stores.stores }));
  }),

  listEmployeesMock((req, res, ctx) => {
    return res(
      ctx.json(
        makeListResults(employees.admins, req.url.searchParams, {
          listName: 'admins',
        }),
      ),
    );
  }),

  createEmployeeMock((_req, res, ctx) => {
    return res(
      ctx.json({
        adminId: '7b8734ef-ed06-4a99-bf22-33adb1dc7df0',
        email: 'test@example.com',
        clientID: null,
        firstName: null,
        lastName: null,
      }),
    );
  }),

  updateEmployeeMock((_req, res, ctx) => {
    return res(ctx.status(200));
  }),

  deleteEmployeeMock((_req, res, ctx) => {
    return res(ctx.status(200));
  }),

  listCategoryMock((req, res, ctx) => {
    return res(
      ctx.json(
        makeListResults(categories.categories, req.url.searchParams, {
          listName: 'categories',
        }),
      ),
    );
  }),

  listTimezonesMock((_req, res, ctx) => {
    return res(ctx.json(timezones));
  }),

  getEmailConfigMock((_req, res, ctx) => {
    return res(ctx.json(clientEmailConfig));
  }),

  getClientMock((_req, res, ctx) => {
    return res(ctx.json(client));
  }),

  clientConfigEmailMock((_req, res, ctx) => {
    return res(ctx.status(200));
  }),

  updatePasswordlessConfigMock((req, res, ctx) => {
    updatePasswordlessBody = req.body;
    return res(ctx.json(req.body));
  }),

  getPasswordlessFileStatusMock((_req, res, ctx) => {
    return res(
      ctx.json({
        android: true,
        ios: true,
      }),
    );
  }),

  updateClientMock((_req, res, ctx) => {
    return res(ctx.status(200));
  }),

  resetPasswordMock((_req, res, ctx) => {
    return res(ctx.status(200));
  }),

  verifyPwResetRequestMock((_req, res, ctx) => {
    return res(ctx.status(200));
  }),

  resetPasswordSubmitMock((_req, res, ctx) => {
    return res(ctx.status(200));
  }),

  listPersonasMock((_req, res, ctx) => {
    return res(ctx.json(personas));
  }),

  listPromoCodesMock((_req, res, ctx) => {
    return res(ctx.json(promoCodes));
  }),
  createPromoCodeMock((_req, res, ctx) => {
    return res(ctx.json(createPromoCode));
  }),
  deletePromoCodeMock((_req, res, ctx) => {
    return res(ctx.status(200));
  }),
  updatePromoCodeMock((_req, res, ctx) => {
    return res(ctx.status(200));
  }),
  getPromoCodeMock((_req, res, ctx) => {
    return res(ctx.json(getPromoCode));
  }),
  addClientEmailDomainMock((_req, res, ctx) => {
    return res(ctx.status(200), ctx.json(addNewClientDomainSuccessResponse));
  }),

  verifyDomainMock((_req, res, ctx) => {
    return res(ctx.status(200), ctx.json(validEmailSuccessResponse));
  }),

  getMerchantMock((_req, res, ctx) => {
    return res(ctx.json(getMerchant));
  }),

  createMerchantMock((_req, res, ctx) => {
    return res(ctx.json({ merchantId: 'merchantId' }));
  }),

  updateMerchantMock((_req, res, ctx) => {
    return res(ctx.status(200));
  }),

  verifyAdminEmailMock((_req, res, ctx) => {
    return res(ctx.status(200), ctx.delay(100));
  }),

  verifyEmployeeMock((_req, res, ctx) => {
    return res(ctx.status(200));
  }),

  createClientDomainMock((req, res, ctx) => {
    clientDomains[newClientDomain.id] = {
      ...newClientDomain,
      domain: req.body.domain,
    };
    return res(ctx.status(201), ctx.json({ ...newClientDomain }));
  }),

  deleteClientDomainMock((req, res, ctx) => {
    clientDomains[req.params.id].status = 'DELETING';
    return res(ctx.status(204));
  }),

  getClientDomainsByClientIdMock((_req, res, ctx) => {
    return res(ctx.status(200), ctx.json(Object.values(clientDomains)));
  }),

  cancelClientDomainMock((req, res, ctx) => {
    clientDomains[req.params.id].status = 'DELETING';
    return res(ctx.status(200));
  }),

  getClientDomainMock((req, res, ctx) => {
    const clientDomain = clientDomains[req.params.id];

    if (!clientDomain) {
      return res(ctx.status(404));
    }

    // Simulate step progression for new client domains
    // This is just for testing purposes to simulate the flow
    if (clientDomain.id === 'new') {
      switch (clientDomain.currentStep) {
        case 'VERIFY_CNAME':
          clientDomain.currentStep = 'PROVISION_TENANT';
          break;
        case 'PROVISION_TENANT':
          clientDomain.currentStep = 'ISSUE_CERT';
          break;
        case 'ISSUE_CERT':
          clientDomain.currentStep = 'UPDATE_DISTRIBUTION';
          break;
        default:
          clientDomain.currentStep = null;
          clientDomain.status = 'ACTIVE';
      }
    }

    if (clientDomain.status === 'DELETING') {
      delete clientDomains[clientDomain.id];
    }

    return res(ctx.status(200), ctx.json(clientDomain));
  }),

  getClientCuisineMock((_req, res, ctx) => {
    return res(ctx.json(clientCuisine));
  }),

  getClientCuisineListMock((_req, res, ctx) =>
    res(ctx.json(benchmarkCuisinesFixture)),
  ),

  getGuestJourneySettingsMock((_req, res, ctx) =>
    res(ctx.json(guestJourneySettingsStore)),
  ),
  getGuestJourneySettingsDefaultsMock((_req, res, ctx) =>
    res(ctx.json(GUEST_JOURNEY_DEFAULT_THRESHOLDS)),
  ),
  // MSW v1: the parsed JSON body is on `req.body` (there is no `req.json()`).
  // Persist the saved settings to the store and echo them back.
  saveGuestJourneySettingsMock((req, res, ctx) => {
    guestJourneySettingsStore = req.body as GuestJourneySettings;
    return res(ctx.json(guestJourneySettingsStore));
  }),
  // Reset keeps the tier names and reverts the thresholds to the defaults.
  resetGuestJourneySettingsMock((_req, res, ctx) => {
    guestJourneySettingsStore = {
      ...guestJourneySettingsStore,
      ...GUEST_JOURNEY_DEFAULT_THRESHOLDS,
    };
    return res(ctx.json(guestJourneySettingsStore));
  }),
  getGuestJourneySettingsHistoryMock((_req, res, ctx) =>
    res(ctx.json(guestJourneySettingsHistoryFixture)),
  ),
];

let updateClientConfigBody;
let updatePasswordlessBody;
